<!--
  The roadmap, for everyone with an account: the version history (v0.1, v0.2 …, newest first),
  what's being implemented soon, and ideas. The treasurer also keeps a private list at the end.
-->
<script lang="ts">
  import * as H from "../settings/hints.ts";
  import { api } from "../lib/api.ts";
  import { app } from "../lib/store.svelte.ts";

  interface Release {
    version: string;
    text: string;
  }
  interface Note {
    id: string;
    text: string;
    done: boolean;
    createdAt: string;
  }

  let releases = $state<Release[]>([]);
  let soon = $state<string[]>([]);
  let ideas = $state<{ text: string; waitingOn: string | null }[]>([]);
  let notes = $state<Note[]>([]);
  let draft = $state("");
  let error = $state("");
  const owner = $derived(app.role === "owner");

  async function load() {
    try {
      const [log, r] = await Promise.all([
        fetch("/changelog.json", { cache: "no-store" }).then((x) => (x.ok ? (x.json() as Promise<Release[]>) : [])),
        api<{ soon: string[]; ideas: { text: string; waitingOn: string | null }[] }>("/api/roadmap"),
      ]);
      releases = log;
      soon = r.soon;
      ideas = r.ideas;
      error = "";
    } catch (e) {
      error = (e as Error).message;
    }
    if (owner) {
      api<{ notes: Note[] }>("/api/admin/notes")
        .then((n) => (notes = n.notes))
        .catch(() => {});
    }
  }
  $effect(() => {
    void load();
  });

  /** v0.50.0 and v0.50.1 go under one heading, "v0.50". */
  const versions = $derived.by(() => {
    const out: { minor: string; items: Release[] }[] = [];
    for (const r of releases) {
      const minor = r.version.split(".").slice(0, 2).join(".");
      const last = out.at(-1);
      if (last?.minor === minor) last.items.push(r);
      else out.push({ minor, items: [r] });
    }
    return out;
  });

  /** The first sentence is the headline; the rest opens under "More". */
  function split(text: string): [string, string] {
    const m = /^(.+?[.!?])\s+(.*)$/.exec(text);
    return m ? [m[1]!, m[2]!] : [text, ""];
  }

  let shown = $state(12);

  async function add(e: SubmitEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    try {
      await api("/api/admin/notes", { body: { text: draft.trim() } });
      draft = "";
      notes = (await api<{ notes: Note[] }>("/api/admin/notes")).notes;
    } catch (err) {
      app.toast((err as Error).message, "error");
    }
  }
  async function toggle(n: Note) {
    n.done = !n.done;
    await api(`/api/admin/notes/${n.id}`, { method: "PATCH", body: { done: n.done } }).catch((e) => app.toast((e as Error).message, "error"));
  }
  async function remove(n: Note) {
    notes = notes.filter((x) => x.id !== n.id);
    await api(`/api/admin/notes/${n.id}`, { method: "DELETE", body: {} }).catch((e) => app.toast((e as Error).message, "error"));
  }
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>Roadmap</h1>
      <p class="lede">What's changed, version by version, what's coming next, and ideas.</p>
    </div>
  </div>
  {#if error}<p class="owes">{error}</p>{/if}

  <nav class="jump" aria-label="On this page">
    <a href="#history">Version history</a>
    <a href="#soon">Implementing soon</a>
    <a href="#ideas">Ideas</a>
  </nav>

  <section id="history" aria-labelledby="history-h">
    <h2 id="history-h">Version history</h2>
    <ol class="versions">
      {#each versions.slice(0, shown) as v (v.minor)}
        <li class="ver">
          <h3><span class="tag">{v.minor}</span></h3>
          <div class="rels">
          {#each v.items as r (r.version)}
            {@const [head, rest] = split(r.text)}
            <div class="rel">
              {#if v.items.length > 1}<span class="patch muted">{r.version}</span>{/if}
              {#if rest}
                <details>
                  <summary>{head}</summary>
                  <p>{rest}</p>
                </details>
              {:else}
                <p class="head-only">{head}</p>
              {/if}
            </div>
          {/each}
          </div>
        </li>
      {/each}
    </ol>
    {#if versions.length > shown}
      <button class="btn small" onclick={() => (shown = versions.length)}>Show all {versions.length} versions</button>
    {/if}
  </section>

  <section id="soon" aria-labelledby="soon-h">
    <h2 id="soon-h">Implementing soon</h2>
    {#if soon.length}
      <ul class="plain">
        {#each soon as t, i (i)}<li>{t}</li>{/each}
      </ul>
    {:else}
      <p class="muted">Nothing queued right now.</p>
    {/if}
  </section>

  <section id="ideas" aria-labelledby="ideas-h">
    <h2 id="ideas-h">Ideas</h2>
    {#if H.ROADMAP_LATER_NOTE.trim()}<p class="muted small">{H.ROADMAP_LATER_NOTE}</p>{/if}
    <ul class="plain">
      {#each ideas as i, k (k)}<li>{i.text}{#if i.waitingOn && owner}<span class="muted small"> · waiting on {i.waitingOn}</span>{/if}</li>{/each}
    </ul>
  </section>

  {#if owner}
    <section class="mine" aria-labelledby="mine-h">
      <h2 id="mine-h">Your list</h2>
      {#if H.ROADMAP_PRIVATE_NOTE.trim()}<p class="muted small">{H.ROADMAP_PRIVATE_NOTE}</p>{/if}
      <form onsubmit={add}>
        <input class="input" bind:value={draft} placeholder="Add an idea or a to-do for yourself" aria-label="New item" />
        <button class="btn" disabled={!draft.trim()}>Add</button>
      </form>
      <ul>
        {#each notes as n (n.id)}
          <li class:done={n.done}>
            <label><input type="checkbox" checked={n.done} onchange={() => toggle(n)} /> <span>{n.text}</span></label>
            <button class="x" aria-label="Delete {n.text}" onclick={() => remove(n)}>×</button>
          </li>
        {:else}
          <li class="muted empty">Nothing here yet.</li>
        {/each}
      </ul>
    </section>
  {/if}
</div>

<style>
  section {
    display: grid;
    gap: 10px;
    margin-bottom: 28px;
    scroll-margin-top: 16px;
  }

  h2 {
    margin: 0;
    font-size: var(--t-xl);
  }

  .jump {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;
  }

  .jump a {
    padding: 6px 12px;
    border-radius: 999px;
    background: var(--wash);
    color: var(--ink);
    font-size: var(--t-sm);
    font-weight: 600;
    text-decoration: none;
  }

  .versions {
    display: grid;
    gap: 0;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .ver {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 4px 14px;
    padding: 12px 0;
    border-top: 1px solid var(--rule);
  }

  .ver h3 {
    margin: 0;
  }

  .rels {
    display: grid;
    gap: 8px;
  }

  .tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--wash);
    font-size: var(--t-sm);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .rel {
    display: grid;
    gap: 2px;
  }

  .patch {
    font-size: var(--t-xs);
    font-variant-numeric: tabular-nums;
  }

  summary {
    cursor: pointer;
    font-weight: 600;
  }

  details p,
  .head-only {
    margin: 4px 0 0;
    color: var(--ink-2);
  }

  .head-only {
    margin: 0;
    color: var(--ink);
    font-weight: 600;
  }

  .plain {
    display: grid;
    gap: 6px;
    margin: 0;
    padding-left: 20px;
  }

  .small {
    font-size: var(--t-sm);
  }

  .mine form {
    display: flex;
    gap: 8px;
  }

  .mine ul {
    display: grid;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .mine li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .mine li.done span {
    text-decoration: line-through;
    color: var(--muted);
  }

  .x {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 18px;
    cursor: pointer;
  }

  @media (max-width: 560px) {
    .ver {
      grid-template-columns: 1fr;
    }

  }
</style>
